'use client';

import { Box, Button, Center, Flex, HStack, IconButton, Portal, Text } from '@chakra-ui/react';
import { ArrowLeft, ChevronDown, FileText, MessageSquare, Plus, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useLivingDocsPanelClose } from '@/contexts/LivingDocsPanelContext';
import { SidePanelToggleButton } from '@/components/core/app-layout/SidePanelToggleButton';
import {
  SidePanelResizeHandle,
  useSidePanelSize,
} from '@/components/core/app-layout/SidePanelSize';
import { useAuth } from '@/app/auth-provider';
import { Menu } from '@chakra-ui/react';
import { formatCodexRelativeTime } from '@/lib/codex-workspace-format';
import type { ReportSummary } from '@/lib/reports/types';
import type { CanvasToolMetadata } from '@/lib/canvas/shared';
import { useProjectCanvasAssets } from '@/hooks/useProjectCanvasAssets';
import { DocsAssetsLibrary } from './DocsAssetsLibrary';
import { LivingReportClient } from './LivingReportClient';
import { ProjectCommentsInbox, type ProjectCommentDocument } from './ProjectCommentsInbox';

interface WorkspaceLivingDocsPanelProps {
  projectId: string;
  activeReportId: string | null;
  open: boolean;
  commentsOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onCommentsOpenChange: (open: boolean) => void;
  onSelectReport: (reportId: string | null) => void;
  canvasAvailable?: boolean;
  onSelectCanvas?: (canvas: CanvasToolMetadata) => void;
}

export function WorkspaceLivingDocsPanel({
  projectId,
  activeReportId,
  open,
  commentsOpen,
  onOpenChange,
  onCommentsOpenChange,
  onSelectReport,
  canvasAvailable = false,
  onSelectCanvas,
}: WorkspaceLivingDocsPanelProps) {
  const { user } = useAuth();
  const authUserRef = useRef(user);
  authUserRef.current = user;
  const userId = user?.uid || null;
  const [reports, setReports] = useState<ReportSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const canvasAssets = useProjectCanvasAssets(projectId, open && !commentsOpen && canvasAvailable);
  const [commentDocuments, setCommentDocuments] = useState<ProjectCommentDocument[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [commentsRefreshing, setCommentsRefreshing] = useState(false);
  const { panelWidthValue: sharedPanelWidth } = useSidePanelSize();
  const loadGenerationRef = useRef(0);
  const commentLoadGenerationRef = useRef(0);
  const createGenerationRef = useRef(0);
  const activeProjectIdRef = useRef(projectId);
  activeProjectIdRef.current = projectId;

  const loadReports = useCallback(async () => {
    const generation = loadGenerationRef.current + 1;
    loadGenerationRef.current = generation;
    const currentUser = authUserRef.current;
    if (!currentUser || !userId || !projectId) {
      setReports([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/reports?projectId=${encodeURIComponent(projectId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await response.json().catch(() => ({}));
      if (generation !== loadGenerationRef.current || activeProjectIdRef.current !== projectId) {
        return;
      }
      if (!response.ok) throw new Error(payload.error || 'Failed to load docs');
      setReports(Array.isArray(payload.reports) ? payload.reports : []);
    } catch (error) {
      if (generation === loadGenerationRef.current && activeProjectIdRef.current === projectId) {
        toast.error(error instanceof Error ? error.message : 'Failed to load docs');
      }
    } finally {
      if (generation === loadGenerationRef.current && activeProjectIdRef.current === projectId) {
        setLoading(false);
      }
    }
  }, [projectId, userId]);

  const loadProjectComments = useCallback(
    async (interactive = false) => {
      const generation = commentLoadGenerationRef.current + 1;
      commentLoadGenerationRef.current = generation;
      const currentUser = authUserRef.current;
      if (!currentUser || !userId || !projectId) {
        setCommentDocuments([]);
        setCommentsLoading(false);
        return;
      }
      if (interactive) setCommentsRefreshing(true);
      else setCommentsLoading(true);
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(
          `/api/reports/comments?projectId=${encodeURIComponent(projectId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const payload = await response.json().catch(() => ({}));
        if (
          generation !== commentLoadGenerationRef.current ||
          activeProjectIdRef.current !== projectId
        ) {
          return;
        }
        if (!response.ok) throw new Error(payload.error || 'Failed to load comments');
        setCommentDocuments(Array.isArray(payload.documents) ? payload.documents : []);
      } catch (error) {
        if (
          generation === commentLoadGenerationRef.current &&
          activeProjectIdRef.current === projectId
        ) {
          toast.error(error instanceof Error ? error.message : 'Failed to load comments');
        }
      } finally {
        if (
          generation === commentLoadGenerationRef.current &&
          activeProjectIdRef.current === projectId
        ) {
          setCommentsLoading(false);
          setCommentsRefreshing(false);
        }
      }
    },
    [projectId, userId]
  );

  useEffect(() => {
    loadGenerationRef.current += 1;
    commentLoadGenerationRef.current += 1;
    createGenerationRef.current += 1;
    setReports([]);
    setCommentDocuments([]);
    setCreating(false);
  }, [projectId]);

  useEffect(() => {
    if (!open || commentsOpen) return;
    void loadReports();
  }, [activeReportId, commentsOpen, loadReports, open]);

  useEffect(() => {
    if (!open || !commentsOpen) return;
    void loadProjectComments();
    const interval = window.setInterval(() => void loadProjectComments(true), 60_000);
    return () => window.clearInterval(interval);
  }, [commentsOpen, loadProjectComments, open]);

  const unresolvedComments = useMemo(
    () =>
      commentDocuments.reduce(
        (total, document) =>
          total +
          document.comments.reduce((count, comment) => count + (comment.resolved ? 0 : 1), 0),
        0
      ),
    [commentDocuments]
  );

  const createReport = useCallback(async () => {
    const currentUser = authUserRef.current;
    if (!currentUser || !userId || !projectId || creating) return;
    const targetProjectId = projectId;
    const generation = createGenerationRef.current + 1;
    createGenerationRef.current = generation;
    setCreating(true);
    try {
      const token = await currentUser.getIdToken();
      if (
        generation !== createGenerationRef.current ||
        activeProjectIdRef.current !== targetProjectId
      ) {
        return;
      }
      const response = await fetch('/api/reports', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: targetProjectId, title: 'Untitled doc' }),
      });
      const payload = await response.json().catch(() => ({}));
      if (
        generation !== createGenerationRef.current ||
        activeProjectIdRef.current !== targetProjectId
      ) {
        return;
      }
      if (!response.ok || !payload.report?.id) {
        throw new Error(payload.error || 'Failed to create doc');
      }
      const report = payload.report as ReportSummary;
      setReports(current => [report, ...current.filter(item => item.id !== report.id)]);
      onSelectReport(report.id);
      onOpenChange(true);
      toast.success('New doc ready.');
    } catch (error) {
      if (
        generation === createGenerationRef.current &&
        activeProjectIdRef.current === targetProjectId
      ) {
        toast.error(error instanceof Error ? error.message : 'Failed to create doc');
      }
    } finally {
      if (generation === createGenerationRef.current) setCreating(false);
    }
  }, [creating, onOpenChange, onSelectReport, projectId, userId]);

  const handleArchived = useCallback(() => {
    setReports(current => current.filter(report => report.id !== activeReportId));
    onSelectReport(null);
    void loadReports();
  }, [activeReportId, loadReports, onSelectReport]);

  const replyToComment = useCallback(
    async (reportId: string, commentId: string, body: string) => {
      const currentUser = authUserRef.current;
      if (!currentUser || !userId) return;
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `/api/reports/${encodeURIComponent(reportId)}/comments/${encodeURIComponent(commentId)}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ body }),
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Failed to reply');
      await loadProjectComments(true);
    },
    [loadProjectComments, userId]
  );

  const resolveComment = useCallback(
    async (reportId: string, commentId: string) => {
      const currentUser = authUserRef.current;
      if (!currentUser || !userId) return;
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `/api/reports/${encodeURIComponent(reportId)}/comments/${encodeURIComponent(commentId)}`,
        {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ resolved: true }),
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Failed to resolve comment');
      await loadProjectComments(true);
    },
    [loadProjectComments, userId]
  );

  useLivingDocsPanelClose(() => onOpenChange(false), open);

  if (!open) return null;

  const docSwitcher = (
    <HStack gap="1">
      <IconButton
        aria-label="Back to Docs & Assets"
        title="Back to Docs & Assets"
        size="sm"
        variant="ghost"
        onClick={() => onSelectReport(null)}
      >
        <ArrowLeft size={16} />
      </IconButton>
      <Menu.Root positioning={{ placement: 'bottom-start', gutter: 8 }}>
        <Menu.Trigger asChild>
          <IconButton
            aria-label="Open another doc"
            title="Open another doc"
            size="sm"
            variant="subtle"
          >
            <FileText size={16} />
            <ChevronDown size={11} />
          </IconButton>
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content
              layerStyle="surfacePopover"
              minW="300px"
              maxW="360px"
              p="1.5"
              fontSize="13px"
            >
              <Menu.Item value="new" onSelect={() => void createReport()}>
                <Plus size={15} /> New doc
              </Menu.Item>
              {reports.map(report => (
                <Menu.Item
                  key={report.id}
                  value={report.id}
                  bg={report.id === activeReportId ? 'ui.surface.selected' : undefined}
                  onSelect={() => onSelectReport(report.id)}
                >
                  <FileText size={15} />
                  <Box minW={0}>
                    <Text fontSize="13px" fontWeight="550" truncate>
                      {report.title}
                    </Text>
                    <Text fontSize="2xs" color="ui.text.tertiary">
                      Updated {formatCodexRelativeTime(report.updatedAt)}
                    </Text>
                  </Box>
                </Menu.Item>
              ))}
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    </HStack>
  );

  return (
    <Flex
      as="aside"
      aria-label="Docs & Assets"
      data-section="workspaceLivingDocPanel"
      position={{ base: 'absolute', lg: 'relative' }}
      inset={{ base: 0, lg: 'auto' }}
      zIndex={{ base: 30, lg: 1 }}
      direction="column"
      w={{ base: 'full', lg: sharedPanelWidth }}
      minW={{ base: 0, lg: sharedPanelWidth }}
      flexShrink={0}
      h="full"
      minH={0}
      borderLeftWidth={{ base: 0, lg: '1px' }}
      borderColor="ui.border.subtle"
      bg="ui.surface.rail"
      boxShadow="var(--attune-shadow-elevated, none)"
      overscrollBehavior="contain"
    >
      <SidePanelResizeHandle label="Resize Docs & Assets" />

      {commentsOpen ? (
        <ProjectCommentsInbox
          documents={commentDocuments}
          loading={commentsLoading}
          refreshing={commentsRefreshing}
          onClose={() => onOpenChange(false)}
          onRefresh={() => void loadProjectComments(true)}
          onReply={replyToComment}
          onResolve={resolveComment}
        />
      ) : activeReportId ? (
        <LivingReportClient
          key={activeReportId}
          reportId={activeReportId}
          embedded
          embeddedNavigation={docSwitcher}
          embeddedActions={
            <IconButton
              aria-label="New doc"
              title="New doc"
              size="sm"
              variant="ghost"
              loading={creating}
              onClick={() => void createReport()}
            >
              <Plus size={16} />
            </IconButton>
          }
          onClose={() => onOpenChange(false)}
          onArchived={handleArchived}
        />
      ) : (
        <Flex direction="column" h="full" minH={0}>
          <HStack
            minH="54px"
            px="3"
            borderBottomWidth="1px"
            borderColor="ui.border.subtle"
            bg="ui.surface.rail"
            justify="space-between"
            flexShrink={0}
          >
            <HStack gap="2">
              <FileText size={17} />
              <Text fontSize="13px" fontWeight="550">
                Docs & Assets
              </Text>
            </HStack>
            <HStack gap="1">
              <Box position="relative">
                <IconButton
                  aria-label={
                    unresolvedComments
                      ? `Open comments, ${unresolvedComments} unresolved`
                      : 'Open comments'
                  }
                  title="Comments"
                  size="sm"
                  variant="ghost"
                  onClick={() => onCommentsOpenChange(true)}
                >
                  <MessageSquare size={16} />
                </IconButton>
                {unresolvedComments ? (
                  <Center
                    position="absolute"
                    top="-1"
                    right="-1"
                    minW="4"
                    h="4"
                    px="1"
                    borderRadius="full"
                    bg="status.danger.fg"
                    color="white"
                    fontSize="9px"
                    fontWeight="800"
                    pointerEvents="none"
                  >
                    {unresolvedComments > 99 ? '99+' : unresolvedComments}
                  </Center>
                ) : null}
              </Box>
              <IconButton
                aria-label="Refresh docs and assets"
                title="Refresh docs and assets"
                size="sm"
                variant="ghost"
                onClick={() => {
                  void loadReports();
                  canvasAssets.refresh();
                }}
              >
                <RefreshCw size={15} />
              </IconButton>
              <SidePanelToggleButton
                open
                label="Hide side panel"
                onClick={() => onOpenChange(false)}
              />
            </HStack>
          </HStack>

          <Box flex="1" minH={0} overflowY="auto" p={{ base: 4, md: 5 }} bg="ui.surface.rail">
            <HStack justify="space-between" align="start" gap="3" mb="5">
              <Box>
                <Text fontSize="lg" fontWeight="600" letterSpacing="-0.025em">
                  Your project library
                </Text>
                <Text mt="1" fontSize="sm" color="ui.text.secondary">
                  Open your work alongside the conversation.
                </Text>
              </Box>
              <Button
                size="sm"
                flexShrink={0}
                loading={creating}
                onClick={() => void createReport()}
              >
                <Plus size={15} /> New doc
              </Button>
            </HStack>
            <DocsAssetsLibrary
              key={projectId}
              reports={reports}
              assets={canvasAssets.assets}
              loading={loading}
              assetsLoading={canvasAssets.loading}
              assetError={canvasAssets.error}
              onRetryAssets={canvasAssets.refresh}
              onOpenReport={onSelectReport}
              onOpenAsset={
                onSelectCanvas
                  ? asset => onSelectCanvas({ ...asset, version: asset.currentVersion })
                  : undefined
              }
            />
          </Box>
        </Flex>
      )}
    </Flex>
  );
}
